import React, { useEffect, useRef } from 'react';
import Webamp from 'webamp';

import { isMuted } from 'lib/sfx';
import { shuffledTracks } from './config';

// Winamp — Kris's playlist (site/tools/music.ts writes config.js from the real files), shuffled on
// every load so a repeat visitor does not get the same opener twice, and started on its own.
//
// Autoplay, honestly: every browser refuses to start audio before the visitor has interacted with
// the page, and there is no way around that and no reason to want one. So we ask once when the
// player is ready — which is enough on its own for anyone whose browser trusts the site — and
// otherwise arm the first click or key anywhere on the desktop to start the music. That gesture is
// ignored when it lands on Winamp itself, so the very first thing a visitor clicks can still be
// the player's own pause button without us fighting them for it.
function Winamp({ onClose, onMinimize }) {
  const ref = useRef(null);
  const webamp = useRef(null);
  useEffect(() => {
    const target = ref.current;
    if (!target) {
      return undefined;
    }
    const instance = new Webamp({ initialTracks: shuffledTracks() });
    webamp.current = instance;

    let armed = false;
    const start = () => {
      try {
        instance.play();
      } catch (e) {
        /* the player is gone, or the browser refused: the next gesture tries again */
      }
    };
    const onGesture = event => {
      const el = event.target;
      const onPlayer = el && el.closest && el.closest('#webamp');
      disarm();
      if (!onPlayer) {
        start();
      }
    };
    function disarm() {
      if (!armed) return;
      armed = false;
      window.removeEventListener('pointerdown', onGesture, true);
      window.removeEventListener('keydown', onGesture, true);
    }

    instance.renderWhenReady(target).then(() => {
      const node = document.querySelector('#webamp');
      if (node) {
        target.appendChild(node);
      }
      // Somebody who muted the desktop's sounds did not ask for 133 minutes of music either.
      if (isMuted()) {
        return;
      }
      start();
      armed = true;
      window.addEventListener('pointerdown', onGesture, true);
      window.addEventListener('keydown', onGesture, true);
    });

    return () => {
      disarm();
      instance.dispose();
      webamp.current = null;
    };
  }, []);
  useEffect(() => {
    if (webamp.current) {
      webamp.current.onClose(onClose);
      webamp.current.onMinimize(onMinimize);
    }
  });
  return (
    <div
      style={{ position: 'fixed', left: 0, top: 0, right: 0, bottom: 0 }}
      ref={ref}
    />
  );
}

export default Winamp;
